import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { fetchGraphQL } from "../../Graphql/utils";
import { useDispatch, useSelector } from "react-redux";
import {
  setPaymentList,
  setPaymentDetail,
  addPayment
} from "../../store/slices/paymentSlice";
import { addReceipt } from "../../store/slices/receiptSlice";
import {
  listInvoicePaymentsByStoreQuery,
  getInvoicePaymentByIdQuery,
  recordPaymentMutation
} from "../../Graphql/querries/invoiceQuery";
import { Payment } from "../../OrderMFE/Payment/types/payment";
import {
  OrderPayment,
  ListOrderPaymentsByStoreResponse,
  GetOrderPaymentByIdResponse,
  RecordPaymentInput,
  RecordPaymentResponse
} from "../../OrderMFE/Invoice/types/invoice";
import { store, RootState } from "../../store/store";
import { FALLBACK_CURRENCY_CODE } from "../../constants/currency";
import {
  PaymentMethod,
  toOmsPaymentMethod
} from "../../OrderMFE/Payment/enums/paymentMethod";
import { OrderStatus } from "../../OrderMFE/Orders/enums/orderStatus";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  API_RESPONSE_CODE,
  AlertBannerContext
} from "@kivunova/kivufrontendcommon";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

function normalizePaymentMethod(method: string): PaymentMethod {
  if (method === "MOBILE_MONEY") return PaymentMethod.MOMO;
  if (method === "BANK_TRANSFER") return PaymentMethod.BANK_DEPOSIT;
  if ((Object.values(PaymentMethod) as string[]).includes(method)) {
    return method as PaymentMethod;
  }
  return PaymentMethod.CASH;
}

function companyCurrencyOrRwf(): string {
  return store.getState().company?.currencyCode ?? FALLBACK_CURRENCY_CODE;
}

/** Maps proxy `OrderPayment` to UI `Payment` (Redux payment slice). */
export function mapOrderPaymentToPayment(o: OrderPayment): Payment {
  return {
    id: o.paymentId,
    paymentNumber: o.paymentNumber ?? o.paymentId,
    documentNumber: o.invoiceNumber,
    invoiceId: o.invoiceId,
    amount: o.amount,
    paymentMethod: normalizePaymentMethod(o.paymentMethod),
    paymentDate: o.paymentDate,
    transactionReference: o.reference ?? undefined,
    currency: companyCurrencyOrRwf(),
    companyId: "",
    status: OrderStatus.COMPLETED,
    createdAt: o.createdAt,
    updatedAt: o.createdAt,
    createdBy: ""
  };
}

/**
 * useGetPaymentList
 * Fetch invoice payments for the current store (proxy: listInvoicePaymentsByStore).
 */
export const useGetPaymentList = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation({
    mutationKey: ["listInvoicePaymentsByStore"],
    mutationFn: async () => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching payment list",
        buildLogContext(
          "payments",
          "list-payments-by-store",
          userId,
          companyId,
          storeId
        )
      );

      try {
        const response = await fetchGraphQL<
          ListOrderPaymentsByStoreResponse,
          Record<string, never>
        >(listInvoicePaymentsByStoreQuery, {}, setAlert);

        const { payments, code } = response.listInvoicePaymentsByStore;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch payment list",
            buildLogContext(
              "payments",
              "list-payments-by-store",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(code)
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return [] as Payment[];
        }

        if (!payments) {
          captureAppException(
            "API returned OK but payments array missing",
            new Error("API returned OK but payments array missing"),
            buildLogContext(
              "payments",
              "list-payments-by-store",
              userId,
              companyId,
              storeId
            )
          );
          return [] as Payment[];
        }

        const mapped = payments.map(mapOrderPaymentToPayment);

        sendAppLog(
          LOG_LEVEL.INFO,
          "Payment list fetched successfully",
          buildLogContext(
            "payments",
            "list-payments-by-store",
            userId,
            companyId,
            storeId,
            {
              paymentCount: mapped.length
            }
          )
        );

        return mapped;
      } catch (error) {
        captureAppException(
          "Failed to fetch payment list",
          error,
          buildLogContext(
            "payments",
            "list-payments-by-store",
            userId,
            companyId,
            storeId
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw error; // preserve mutation.isError; prevents onSuccess from firing
      }
    },
    onSuccess: (list: Payment[]) => {
      dispatch(setPaymentList(list));
    }
  });
};

/**
 * useGetPaymentDetail
 * Get invoice payment by id (proxy: getInvoicePaymentById).
 */
export const useGetPaymentDetail = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation({
    mutationKey: ["getInvoicePaymentById"],
    mutationFn: async (input: { paymentId: string }) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching payment detail",
        buildLogContext(
          "payments",
          "get-payment-by-id",
          userId,
          companyId,
          storeId,
          {
            paymentId: input.paymentId
          }
        )
      );

      try {
        const response = await fetchGraphQL<
          GetOrderPaymentByIdResponse,
          { input: { paymentId: string } }
        >(getInvoicePaymentByIdQuery, { input }, setAlert);

        const { payment, code } = response.getInvoicePaymentById;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch payment detail",
            buildLogContext(
              "payments",
              "get-payment-by-id",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(code),
                paymentId: input.paymentId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!payment) {
          captureAppException(
            "API returned OK but payment object missing",
            new Error("API returned OK but payment object missing"),
            buildLogContext(
              "payments",
              "get-payment-by-id",
              userId,
              companyId,
              storeId,
              {
                paymentId: input.paymentId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        const mapped = mapOrderPaymentToPayment(payment);

        sendAppLog(
          LOG_LEVEL.INFO,
          "Payment detail fetched successfully",
          buildLogContext(
            "payments",
            "get-payment-by-id",
            userId,
            companyId,
            storeId,
            {
              paymentId: payment.paymentId
            }
          )
        );

        return mapped;
      } catch (error) {
        captureAppException(
          "Failed to fetch payment detail",
          error,
          buildLogContext(
            "payments",
            "get-payment-by-id",
            userId,
            companyId,
            storeId,
            {
              paymentId: input.paymentId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw error; // preserve mutation.isError; prevents onSuccess from firing
      }
    },
    onSuccess: (payment: Payment | null) => {
      if (payment) dispatch(setPaymentDetail(payment));
    }
  });
};

/**
 * useCreatePayment
 * Records a payment against an invoice (proxy: recordPayment).
 */
export const useCreatePayment = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation({
    mutationKey: ["recordPayment"],
    mutationFn: async (input: RecordPaymentInput) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      const apiInput = {
        invoiceId: input.invoiceId,
        amount: input.amount,
        paymentDate: input.paymentDate,
        reference: input.reference,
        paymentMethod: toOmsPaymentMethod(input.paymentMethod)
      };

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started creating payment",
        buildLogContext(
          "payments",
          "create-payment",
          userId,
          companyId,
          storeId,
          {
            invoiceId: apiInput.invoiceId,
            amount: apiInput.amount,
            paymentMethod: apiInput.paymentMethod as string
          }
        )
      );

      try {
        const response = await fetchGraphQL<
          RecordPaymentResponse,
          { input: typeof apiInput }
        >(recordPaymentMutation, { input: apiInput }, setAlert);

        const { code, invoice, receipt } = response.recordPayment;

        if (code !== API_RESPONSE_CODE.OK || !invoice?.payments?.length) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Create payment rejected by API",
            buildLogContext(
              "payments",
              "create-payment",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(code),
                invoiceId: input.invoiceId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (receipt) {
          dispatch(addReceipt(receipt));
        }

        const last = invoice.payments[invoice.payments.length - 1];
        const mapped = mapOrderPaymentToPayment(last);

        sendAppLog(
          LOG_LEVEL.INFO,
          "Payment created successfully",
          buildLogContext(
            "payments",
            "create-payment",
            userId,
            companyId,
            storeId,
            {
              invoiceId: input.invoiceId,
              paymentId: last.paymentId,
              receiptId: receipt?.receiptId ?? ""
            }
          )
        );

        return mapped;
      } catch (error) {
        captureAppException(
          "Failed to create payment",
          error,
          buildLogContext(
            "payments",
            "create-payment",
            userId,
            companyId,
            storeId,
            {
              invoiceId: input.invoiceId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw error; // preserve mutation.isError; prevents onSuccess from firing
      }
    },
    onSuccess: (payment: Payment | null) => {
      if (payment) dispatch(addPayment(payment));
    }
  });
};
